import { handleError } from "@app/util/handle-error";
import {
  ProductsApi,
  OutProductDTO,
  OutAdminProductExtendedViewDTO,
  CreateProductDTO,
  InProductDTO,
  GetAllProductsExtendedSortByEnum,
  GetAllProductsExtendedSortOrderEnum
} from "@app/api/generated";
import { getAuthorizedApiConfig } from "@app/util/api-config";
import { AppThunk } from "@app/redux/store";

import { productsActions } from "../reducers/products";

const getProducts =
  (
    skip: number,
    take: number,
    platformId?: number,
    searchQuery?: string,
    status?: number,
    sortBy?: GetAllProductsExtendedSortByEnum,
    sortOrder?: GetAllProductsExtendedSortOrderEnum
  ): AppThunk =>
  async (dispatch): Promise<void> => {
    try {
      dispatch(productsActions.setIsLoading(true));

      const api = new ProductsApi(await getAuthorizedApiConfig());
      const isLive = !status ? undefined : status === 1 ? "true" : "false";
      const resultFromApi = await api.getAllProductsExtended({
        take,
        skip,
        searchQuery,
        platformId,
        live: isLive,
        sortBy,
        sortOrder
      });

      dispatch(productsActions.setProducts(resultFromApi.products || []));
      dispatch(productsActions.setTotalResults(resultFromApi.total));
      dispatch(productsActions.setTotalPages(Math.ceil(resultFromApi.total / take)));
    } catch (error) {
      handleError(error, dispatch, "Failed to get products");
    } finally {
      // Always clear the spinner — a failed/403'd fetch must not leave the overview loading forever.
      dispatch(productsActions.setIsLoading(false));
    }
  };

const getProduct =
  (id: number): AppThunk =>
  async (dispatch): Promise<OutAdminProductExtendedViewDTO | undefined> => {
    try {
      const api = new ProductsApi(await getAuthorizedApiConfig());

      const resultFromApi = await api.getProductById({ extendedView: true, id });

      dispatch(productsActions.setSelectedProduct(resultFromApi));

      return resultFromApi;
    } catch (error) {
      handleError(error, dispatch, "Failed to get product");

      return undefined;
    }
  };

const deleteProduct =
  (id: number): AppThunk =>
  async (dispatch): Promise<void> => {
    try {
      const api = new ProductsApi(await getAuthorizedApiConfig());

      const isDeleted = await api.deleteProduct({ id });

      if (isDeleted) {
        dispatch(productsActions.deleteProductFromList(id));
      }
    } catch (error) {
      handleError(error, dispatch, "Failed to delete product");
    }
  };

const createProduct =
  (product: CreateProductDTO): AppThunk =>
  async (dispatch): Promise<OutProductDTO | undefined> => {
    try {
      const api = new ProductsApi(await getAuthorizedApiConfig());
      const createdProduct = await api.createProduct({ createProductDTO: product });

      // Don't put the base product in the store: createProduct returns no admin lifecycle fields.
      // The detail page navigates to /products/:id right after, which fetches the full admin view.
      return createdProduct;
    } catch (error) {
      handleError(error, dispatch, "Failed to create new product");
    }

    return undefined;
  };

const saveProduct =
  (product: InProductDTO): AppThunk =>
  async (dispatch): Promise<OutProductDTO | undefined> => {
    try {
      const api = new ProductsApi(await getAuthorizedApiConfig());
      const savedProduct = await api.updateProduct({ id: product.id, inProductDTO: product });

      // updateProduct returns the base product, without the admin lifecycle fields. The detail page
      // stays put after a save, so re-fetch the admin view — otherwise the sidebar reads
      // createdBy/noDealsSince/offlineAt/deleteAt as undefined and flips to wrong data.
      const adminProduct = await api.getProductById({ extendedView: true, id: product.id });
      dispatch(productsActions.setSelectedProduct(adminProduct));

      return savedProduct;
    } catch (error: any) {
      const e = await error.json();
      let message = "Failed to save product";

      if (e?.message?.toLocaleLowerCase()?.includes("duplicate")) {
        message = "Slug already exists, choose another one";
      }

      handleError(error, dispatch, message);
    }
  };

export const productsThunk = {
  getProducts,
  getProduct,
  deleteProduct,
  createProduct,
  saveProduct
};
